import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Grid,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Close,
  Medication,
  Search,
  LocalHospital,
  Warning,
  CheckCircle,
  Schedule,
  FilterList,
  GridView as GridViewIcon,
  ViewList as ViewListIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getMedicationSheetHistorydb } from '../BaseURL';
import moment from "moment";

// Return a moment for the dose's scheduled time. Prefer the canonical
// `due_at` DATETIME (set when the chart is generated). Fall back to the
// legacy `${d_dat} ${time}` for older rows that pre-date the schema change.
const dueMoment = (record) => {
  if (record?.due_at) {
    const m = moment(record.due_at);
    if (m.isValid()) return m;
  }
  return moment(`${record?.d_dat || ''} ${record?.time || ''}`, 'YYYY-MM-DD hh:mm:ss A');
};

const MedicationSheetDialog = ({ open, onClose, patientData, startDate }) => {
  const currentDate = moment().format('YYYY-MM-DD');
  const [loading, setLoading] = useState(false);
  const [medicationSheet, setMedicationSheet] = useState([]);
  const [filteredSheet, setFilteredSheet] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('today');     // default to today → less scroll
  const [viewMode, setViewMode]     = useState('grid');      // 'grid' (MAR) | 'list'
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (open && patientData && patientData.id) {
      loadMedicationSheet();
    } else {
      // Reset state when dialog closes
      setMedicationSheet([]);
      setFilteredSheet([]);
      setError(null);
      setSearchTerm('');
      setStatusFilter('all');
      setDateFilter('today');
      setViewMode('grid');
      setActiveTab(0);
    }
  }, [open, patientData]);

  useEffect(() => {
    filterMedicationSheet();
  }, [medicationSheet, searchTerm, statusFilter, dateFilter]);

  const loadMedicationSheet = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(getMedicationSheetHistorydb, {
        params: { 
          patientId: patientData.id, 
          startDate: startDate || '1900-01-01',
          endDate: currentDate 
        }
      });

      if (response.data.success) {
        setMedicationSheet(response.data.medicationSheet || []);
      } else {
        setError(response.data.message || 'Failed to load medication sheet');
      }
    } catch (err) {
      console.error('Error loading medication sheet:', err);
      setError('Failed to load medication sheet. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterMedicationSheet = () => {
    let filtered = [...medicationSheet];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(med => 
        med.med.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(med => {
        const isServed = med.s_by && med.s_by !== '-' && med.s_by !== 'i';
        const isPast = dueMoment(med).isBefore(moment());
        
        if (statusFilter === 'served') return isServed;
        if (statusFilter === 'pending') return !isServed && !isPast;
        if (statusFilter === 'missed') return !isServed && isPast;
        return true;
      });
    }

    // Date filter
    if (dateFilter !== 'all') {
      const today = moment().startOf('day');
      filtered = filtered.filter(med => {
        const medDate = moment(med.d_dat);
        
        if (dateFilter === 'today') return medDate.isSame(today, 'day');
        if (dateFilter === 'yesterday') return medDate.isSame(today.clone().subtract(1, 'day'), 'day');
        if (dateFilter === 'week') return medDate.isAfter(today.clone().subtract(7, 'days'));
        if (dateFilter === 'month') return medDate.isAfter(today.clone().subtract(30, 'days'));
        return true;
      });
    }

    setFilteredSheet(filtered);
  };

  // Calculate statistics
  const totalEntries = medicationSheet.length;
  const servedCount = medicationSheet.filter(m => m.s_by && m.s_by !== '-' && m.s_by !== 'i').length;
  const pendingCount = medicationSheet.filter(m => {
    const isServed = m.s_by && m.s_by !== '-' && m.s_by !== 'i';
    const isPast = dueMoment(m).isBefore(moment());
    return !isServed && !isPast;
  }).length;
  const missedCount = medicationSheet.filter(m => {
    const isServed = m.s_by && m.s_by !== '-' && m.s_by !== 'i';
    const isPast = dueMoment(m).isBefore(moment());
    return !isServed && isPast;
  }).length;

  // Get unique medications
  const uniqueMedications = [...new Set(medicationSheet.map(m => m.med.split(' ')[0]))];

  // ── MAR grid grouping ────────────────────────────────────────────────
  // Group filtered rows by medication name. Each group holds the dose
  // entries for that med, sorted chronologically. The first word of `med`
  // is the drug name; the rest of the string carries dose/freq/route.
  const groupedByMed = useMemo(() => {
    const groups = new Map();
    filteredSheet.forEach(rec => {
      const fullMed = rec.med || '';
      const drugName = fullMed.split(' ')[0] || 'Unknown';
      // Anything after the drug name is the dosing detail line.
      const detail = fullMed.slice(drugName.length).trim();
      if (!groups.has(drugName)) {
        groups.set(drugName, { drugName, detail, doses: [] });
      }
      groups.get(drugName).doses.push(rec);
    });
    const arr = [...groups.values()];
    arr.forEach(g => g.doses.sort((a, b) => dueMoment(a).valueOf() - dueMoment(b).valueOf()));
    return arr;
  }, [filteredSheet]);

  if (!patientData) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: '#1A9B7F',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2,
          px: 2.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Medication />
          <Box>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
              Medication Administration Sheet
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', opacity: 0.9 }}>
              {patientData.fullName} · ID {patientData.id}
              {patientData.age != null ? ` · ${patientData.age} yrs` : ''}
              {patientData.gender ? ` · ${patientData.gender}` : ''}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'white',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' }
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading medication sheet history...</Typography>
          </Box>
        )}

        {/* Error State */}
        {error && !loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="error" gutterBottom>
              {error}
            </Typography>
            <Button variant="contained" onClick={loadMedicationSheet} sx={{ mt: 2 }}>
              Retry
            </Button>
          </Box>
        )}

        {/* Main Content */}
        {!loading && !error && (
          <>
            {/* Patient Info Card */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: 'primary.main',
                    width: 56,
                    height: 56,
                    fontSize: 20,
                    fontWeight: 700
                  }}
                >
                  {patientData.fullName
                    ?.split(" ")
                    .map((n) => n[0])
                    .join("")
                    .slice(0, 2)}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {patientData.fullName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID: {patientData.id} • Age: {patientData.age} • Gender: {patientData.gender} • Tel: {patientData.tel}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Summary Statistics */}
            {medicationSheet.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography sx={{
                  fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8',
                  textTransform: 'uppercase', letterSpacing: '0.6px', mb: 1.25,
                }}>
                  Summary
                </Typography>
                <Grid container spacing={1.5}>
                  {[
                    { label: 'Total Entries',     value: totalEntries,           color: '#0f172a', tint: '#f1f5f9' },
                    { label: 'Served',            value: servedCount,            color: '#15803D', tint: 'rgba(21,128,61,0.10)' },
                    { label: 'Pending',           value: pendingCount,           color: '#A16207', tint: 'rgba(217,119,6,0.10)' },
                    { label: 'Missed',            value: missedCount,            color: '#991B1B', tint: 'rgba(220,38,38,0.10)' },
                    { label: 'Unique Meds',       value: uniqueMedications.length, color: '#1A9B7F', tint: 'rgba(26,155,127,0.10)' },
                  ].map(t => (
                    <Grid item xs={6} sm={4} md={2.4} key={t.label}>
                      <Paper elevation={0} sx={{
                        p: 1.5, borderRadius: 2,
                        border: '1px solid #e5e9ef', bgcolor: '#fff',
                      }}>
                        <Typography sx={{
                          fontSize: '0.66rem', fontWeight: 700, color: '#94a3b8',
                          textTransform: 'uppercase', letterSpacing: '0.5px',
                        }}>
                          {t.label}
                        </Typography>
                        <Box sx={{
                          display: 'inline-block', mt: 0.75,
                          px: 1, py: 0.25, borderRadius: 1,
                          bgcolor: t.tint,
                        }}>
                          <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: t.color, lineHeight: 1.1 }}>
                            {t.value}
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search medication..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        )
                      }}
                    />
                    <ToggleButtonGroup
                      size="small"
                      value={viewMode}
                      exclusive
                      onChange={(_, v) => v && setViewMode(v)}
                      sx={{ flexShrink: 0 }}
                    >
                      <ToggleButton value="grid" sx={{ px: 1.25, '&.Mui-selected': { bgcolor: 'rgba(26,155,127,0.10)', color: '#1A9B7F' } }}>
                        <Tooltip title="Grid view (one row per medication)" arrow>
                          <GridViewIcon fontSize="small" />
                        </Tooltip>
                      </ToggleButton>
                      <ToggleButton value="list" sx={{ px: 1.25, '&.Mui-selected': { bgcolor: 'rgba(26,155,127,0.10)', color: '#1A9B7F' } }}>
                        <Tooltip title="List view (one row per dose)" arrow>
                          <ViewListIcon fontSize="small" />
                        </Tooltip>
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status Filter</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      label="Status Filter"
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="served">Served</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="missed">Missed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Date Filter</InputLabel>
                    <Select
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value)}
                      label="Date Filter"
                    >
                      <MenuItem value="all">All Dates</MenuItem>
                      <MenuItem value="today">Today</MenuItem>
                      <MenuItem value="yesterday">Yesterday</MenuItem>
                      <MenuItem value="week">Last 7 Days</MenuItem>
                      <MenuItem value="month">Last 30 Days</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            {/* Results Summary */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredSheet.length} of {totalEntries} entries
              </Typography>
              {(searchTerm || statusFilter !== 'all' || dateFilter !== 'all') && (
                <Button 
                  size="small" 
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setDateFilter('all');
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>

            {/* MAR Grid View — one row per medication, dose chips per slot */}
            {viewMode === 'grid' && (
              <Box sx={{ border: '1px solid #e5e9ef', borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>
                {groupedByMed.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 5 }}>
                    <Medication sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                    <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
                      {medicationSheet.length === 0
                        ? 'No medication sheet history available'
                        : 'No medications match your filters'}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{
                    maxHeight: '52vh',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                  }}>
                    {groupedByMed.map((g, gi) => (
                      <Box key={g.drugName + gi} sx={{
                        display: 'flex',
                        borderBottom: gi === groupedByMed.length - 1 ? 'none' : '1px solid #e5e9ef',
                        '&:hover': { bgcolor: '#f8fafc' },
                      }}>
                        {/* Sticky med name column */}
                        <Box sx={{
                          width: 240, flexShrink: 0,
                          p: 1.5,
                          borderRight: '1px solid #e5e9ef',
                          bgcolor: '#fff',
                          position: 'sticky',
                          left: 0,
                          zIndex: 1,
                        }}>
                          <Typography sx={{
                            fontSize: '0.92rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2,
                          }}>
                            {g.drugName}
                          </Typography>
                          <Typography noWrap sx={{
                            fontSize: '0.72rem', color: '#64748b', mt: 0.25,
                          }}>
                            {g.detail || '—'}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                            {(() => {
                              const served = g.doses.filter(d => d.s_by && d.s_by !== '-' && d.s_by !== 'i').length;
                              const missed = g.doses.filter(d => {
                                const isServed = d.s_by && d.s_by !== '-' && d.s_by !== 'i';
                                return !isServed && dueMoment(d).isBefore(moment());
                              }).length;
                              const pending = g.doses.length - served - missed;
                              return (
                                <>
                                  {served > 0 && <Chip size="small" label={`${served} given`} sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: 'rgba(22,163,74,0.10)', color: '#15803D' }} />}
                                  {pending > 0 && <Chip size="small" label={`${pending} due`} sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: 'rgba(217,119,6,0.10)', color: '#A16207' }} />}
                                  {missed > 0 && <Chip size="small" label={`${missed} missed`} sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: 'rgba(220,38,38,0.10)', color: '#991B1B' }} />}
                                </>
                              );
                            })()}
                          </Box>
                        </Box>

                        {/* Horizontally scrollable dose chips */}
                        <Box sx={{
                          flex: 1, minWidth: 0,
                          p: 1, display: 'flex', gap: 0.75,
                          overflowX: 'auto',
                          alignItems: 'center',
                        }}>
                          {g.doses.map((d, di) => {
                            const isServed = d.s_by && d.s_by !== '-' && d.s_by !== 'i';
                            const m = dueMoment(d);
                            const isPast = m.isBefore(moment());
                            const isMissed = !isServed && isPast;
                            const status = isServed ? 'served' : isMissed ? 'missed' : 'pending';

                            const tone = {
                              served:  { bg: 'rgba(22,163,74,0.10)',  border: '#86EFAC', icon: '✓', color: '#15803D' },
                              missed:  { bg: 'rgba(220,38,38,0.10)',  border: '#FCA5A5', icon: '⚠', color: '#991B1B' },
                              pending: { bg: 'rgba(217,119,6,0.10)',  border: '#FCD34D', icon: '⏳', color: '#A16207' },
                            }[status];

                            const tip = (
                              <Box sx={{ p: 0.5, fontSize: '0.78rem', lineHeight: 1.5 }}>
                                <div><strong>{m.format('ddd D MMM, hh:mm A')}</strong></div>
                                <div>Status: <strong style={{ textTransform: 'capitalize' }}>{status}</strong></div>
                                {isServed && (
                                  <>
                                    <div>Given by: {d.s_by}</div>
                                    {d.served_at && moment(d.served_at).isValid() && (
                                      <div>At: {moment(d.served_at).format('hh:mm A')}</div>
                                    )}
                                  </>
                                )}
                                <div>Charted by: {d.user || '—'}</div>
                              </Box>
                            );

                            return (
                              <Tooltip key={d.code || di} title={tip} arrow placement="top">
                                <Box sx={{
                                  flexShrink: 0,
                                  minWidth: 88,
                                  px: 1, py: 0.75,
                                  borderRadius: 1.5,
                                  bgcolor: tone.bg,
                                  border: `1px solid ${tone.border}`,
                                  display: 'flex', alignItems: 'center', gap: 0.75,
                                  cursor: 'default',
                                }}>
                                  <Typography sx={{ fontSize: '0.85rem', lineHeight: 1 }}>{tone.icon}</Typography>
                                  <Box>
                                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: tone.color, lineHeight: 1.1 }}>
                                      {m.format('HH:mm')}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.6rem', color: tone.color, opacity: 0.85, lineHeight: 1.1 }}>
                                      {m.format('D MMM')}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Tooltip>
                            );
                          })}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {/* Medication Sheet Table */}
            {viewMode === 'list' && (
            <TableContainer component={Paper} variant="outlined">
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200' }}>Time</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200' }}>Medication</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200' }}>Served By</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200' }}>Charted By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSheet.length > 0 ? (
                    filteredSheet.map((record, idx) => {
                      const isServed = record.s_by && record.s_by !== '-' && record.s_by !== 'i';
                      const isPast = dueMoment(record).isBefore(moment());
                      const isMissed = !isServed && isPast;
                      const scheduled = dueMoment(record);
                      const scheduledLabel = scheduled.isValid() ? scheduled.format('hh:mm A') : (record.time || '—');
                      const servedAtLabel = record.served_at && moment(record.served_at).isValid()
                        ? moment(record.served_at).format('YYYY-MM-DD hh:mm A')
                        : null;
                      
                      return (
                        <TableRow 
                          key={idx}
                          hover
                          sx={{ 
                            '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
                            bgcolor: isServed 
                              ? 'rgba(76, 175, 80, 0.08)' 
                              : isMissed 
                                ? 'rgba(244, 67, 54, 0.08)' 
                                : 'inherit'
                          }}
                        >
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>
                            <Chip 
                              label={record.dat} 
                              size="small"
                              color={record.dat === moment().format('MM/DD') ? 'primary' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {scheduledLabel}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography 
                              variant="body2"
                              sx={{
                                maxWidth: 400,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {record.med}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {isServed ? (
                              <Chip 
                                label="Served" 
                                color="success" 
                                size="small"
                                icon={<CheckCircle />}
                              />
                            ) : isMissed ? (
                              <Chip 
                                label="Missed" 
                                color="error" 
                                size="small"
                                icon={<Warning />}
                              />
                            ) : (
                              <Chip 
                                label="Pending" 
                                color="warning" 
                                size="small"
                                icon={<Schedule />}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color={isServed ? 'success.dark' : 'text.secondary'}
                              fontWeight={isServed ? 600 : 400}
                            >
                              {isServed ? record.s_by : '-'}
                            </Typography>
                            {isServed && servedAtLabel && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                {servedAtLabel}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {record.user || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Medication sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          {medicationSheet.length === 0 
                            ? "No medication sheet history available"
                            : "No medications match your filters"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ bgcolor: 'grey.50', p: 2 }}>
        <Button 
          onClick={onClose} 
          variant="contained" 
          color="primary"
          size="large"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MedicationSheetDialog;